/* Copyright (c) 2020-present CloudIO Inc */

import { CommonProps } from '../layout/Sidebar/panels/PropertiesView/CommonProperties';
import { getManager } from '../registry/Manager';
import { Platform } from '../platform/Platform';
import {
  ComponentSample,
  IconValue,
  PropertyDefinitions,
  TriggerManager,
  TriggerManagerFn,
} from '../registry/Component.type';
import {
  ActionItemType,
  ItemType,
  TriggerItemType,
  TriggerItemTypeRegistry,
} from '../registry/ComponentRegistry';
import { baseTriggerManager } from '../registry/Components';
import { triggerDefaultValue } from './Triggers';
import { analyzeTrigger } from './TriggerAnalyze';
import { responsiveTriggersHelpRenders } from './TriggerOnWindowSizeChange_Help';

export const triggerIcons: Record<string, IconValue> = {
  TriggerOnClick: { iconName: 'hand-pointer' },
  TriggerOnExtraLargeScreen: { iconName: 'desktop' },
  TriggerOnLargeScreen: { iconName: 'laptop' },
  TriggerOnMediumScreen: { iconName: 'tablet' },
  TriggerOnPageActive: { iconName: 'portal-enter' },
  TriggerOnPageExit: { iconName: 'portal-exit' },
  TriggerOnWindowSizeChange: { iconName: 'expand' },
  TriggerOnLandscapeScreen: { iconName: 'rectangle-landscape' },
  TriggerOnPortraitScreen: { iconName: 'rectangle-portrait' },
  TriggerOnSmallScreen: { iconName: 'mobile-notch' },
  TriggerOnExtraSmallScreen: { iconName: 'mobile-screen-button' },
  TriggerAfterSave: { iconName: 'save' },
  TriggerBeforeSave: { iconName: 'save' },
  TriggerBeforeCreate: { iconName: 'plus' },
  TriggerAfterQuery: { iconName: 'redo' },
  TriggerBeforeQuery: { iconName: 'undo' },
};

export const triggerNames: Record<TriggerItemType, string> = {
  TriggerAfterServerValidate: 'Trigger After Server Validate',
  TriggerAfterUpload: 'Trigger After Upload',
  TriggerAfterPaste: 'Trigger After Paste',
  TriggerOnPaste: 'Trigger On Paste',
  TriggerOnDeleteKeyPress: 'Trigger On Delete Key Press',
  TriggerOnEnterKeyPress: 'Trigger On Enter Key Press',
  TriggerOnRecordActive: 'Trigger On Record Active',
  TriggerAfterDeleteRecord: 'Trigger After Delete Record',
  TriggerBeforeDeleteRecord: 'Trigger Before Delete Record',
  TriggerAfterInsertStoreRecord: 'Trigger After Insert Store Record',
  TriggerBeforeInsertStoreRecord: 'Trigger Before Insert Store Record',
  TriggerOnIconClick: 'Trigger On Icon Click',
  TriggerOnTreeColumnExpand: 'Trigger On Tree Column Expand',
  TriggerOnMention: 'Trigger On Mention',
  TriggerAfterLoad: 'Trigger After Load',
  TriggerAfterQuery: 'Trigger After Query',
  TriggerAfterSave: 'Trigger After Save',
  TriggerBeforeCreate: 'Trigger Before Create',
  TriggerBeforeLoad: 'Trigger Before Load',
  TriggerBeforeQuery: 'Trigger Before Query',
  TriggerBeforeSave: 'Trigger Before Save',
  TriggerOnAggregateClick: 'Trigger On Aggregate Click',
  TriggerOnBlur: 'Trigger On Blur',
  TriggerOnCellClick: 'Trigger On Cell Click',
  TriggerOnChange: 'Trigger On Change',
  TriggerOnClick: 'Trigger On Click',
  TriggerOnComment: 'Trigger On Comment',
  TriggerOnCreate: 'Trigger On Create',
  TriggerOnDelete: 'Trigger On Delete',
  TriggerOnDoubleClick: 'Trigger On Double Click',
  TriggerOnDrop: 'Trigger On Drop',
  TriggerOnDateChange: 'Trigger On Date Change',
  TriggerOnEventChange: 'Trigger On Event Change',
  TriggerOnEventClick: 'Trigger On Event Click',
  TriggerOnEventCreate: 'Trigger On Event Create',
  TriggerOnExtraLargeScreen: 'Trigger On Extra Large Screen',
  TriggerOnExtraSmallScreen: 'Trigger On Extra Small Screen',
  TriggerOnFocus: 'Trigger On Focus',
  TriggerOnInitialize: 'Trigger On Initialize',
  TriggerOnInterval: 'Trigger On Interval',
  TriggerOnKeyDown: 'Trigger On Key Down',
  TriggerOnLandscapeScreen: 'Trigger On Landscape Screen',
  TriggerOnLargeScreen: 'Trigger On Large Screen',
  TriggerOnMediumScreen: 'Trigger On Medium Screen',
  TriggerOnMouseEnter: 'Trigger On Mouse Enter',
  TriggerOnMouseLeave: 'Trigger On Mouse Leave',
  TriggerOnPageActive: 'Trigger On Page Active',
  TriggerOnPageExit: 'Trigger On Page Exit',
  TriggerOnPopoverClose: 'Trigger On Close',
  TriggerOnPopoverOpen: 'Trigger On Open',
  TriggerOnPopupClose: 'Trigger On Close',
  TriggerOnPopupOpen: 'Trigger On Open',
  TriggerOnPortraitScreen: 'Trigger On Portrait Screen',
  TriggerOnResponse: 'Trigger On Response',
  TriggerOnRowClick: 'Trigger On Row Click',
  TriggerOnRowDoubleClick: 'Trigger On Row Double Click',
  TriggerOnSearch: 'Trigger On Search',
  TriggerOnSmallScreen: 'Trigger On Small Screen',
  TriggerOnSubmit: 'Trigger On Submit',
  TriggerOnSubscriptionEvent: 'Trigger On Subscription Event',
  TriggerOnTabActive: 'Trigger On Tab Active',
  TriggerOnTabExit: 'Trigger On Tab Exit',
  TriggerOnValueChange: 'Trigger On Value Change',
  TriggerOnWindowSizeChange: 'Trigger On Window Size Change',
};

export const helpRenders: Record<string, ComponentSample<any>[]> = {
  TriggerOnWindowSizeChange: responsiveTriggersHelpRenders(
    'TriggerOnWindowSizeChange',
  ),
  TriggerOnLargeScreen: responsiveTriggersHelpRenders('TriggerOnLargeScreen'),
  TriggerOnMediumScreen: responsiveTriggersHelpRenders('TriggerOnMediumScreen'),
  TriggerOnSmallScreen: responsiveTriggersHelpRenders('TriggerOnSmallScreen'),
  TriggerOnExtraLargeScreen: responsiveTriggersHelpRenders(
    'TriggerOnExtraLargeScreen',
  ),
  TriggerOnExtraSmallScreen: responsiveTriggersHelpRenders(
    'TriggerOnExtraSmallScreen',
  ),
  TriggerOnPortraitScreen: responsiveTriggersHelpRenders(
    'TriggerOnPortraitScreen',
  ),
  TriggerOnLandscapeScreen: responsiveTriggersHelpRenders(
    'TriggerOnLandscapeScreen',
  ),
};

export const triggerDesc: Record<TriggerItemType, string> = {
  TriggerAfterServerValidate:
    'Use this trigger to perform actions after server validate',
  TriggerAfterUpload:
    'Use this trigger to perform actions after records are uploaded',
  TriggerAfterPaste:
    'Use this trigger to perform actions after the records are pasted into the data grid.',
  TriggerOnPaste:
    'Use this trigger to perform actions when values are pasted into a data grid.',
  TriggerOnDeleteKeyPress:
    'Use this trigger to perform actions when you press delete.',
  TriggerOnEnterKeyPress:
    'Use this trigger to perform actions when you press enter.',
  TriggerOnRecordActive: `Use this trigger to perform actions when a record gets active.
  \`\`\`javascript
// Access the active record information as below using expression
\`\${$._.data.record} \${$._.data.recordId} \${$._.data.rowIndex}\`
\`\`\`
`,
  TriggerAfterDeleteRecord:
    'Use this trigger to perform actions after a deleted record is posted to the server. It is fired for each record that got deleted. When this trigger is fired, the record is already hidden from the UI and removed from the Store',
  TriggerBeforeDeleteRecord:
    'Use this trigger to perform actions before a deleted record is posted to the server. It is fired for each record that is being deleted. When this trigger is fired, the record is already hidden from the UI.',
  TriggerAfterInsertStoreRecord: `Use this trigger to perform actions after a newly inserted record is posted to the server. It is fired for each record that is being inserted.
    This trigger is fired before "Trigger After Data Store Save"`,
  TriggerBeforeInsertStoreRecord: `Use this trigger to perform actions before a newly inserted record is posted to the server. It is fired for each record that is being inserted.
    This trigger is fired after "Trigger Before Save"`,
  TriggerOnIconClick:
    'Use this trigger to perform actions when the user clicks on end icon of Tree Column',
  TriggerOnTreeColumnExpand:
    'Use this trigger to perform actions when user expanded any tree column row',
  TriggerOnAggregateClick:
    'Use this trigger to perform actions when the user clicks on an aggregate value on the Pivot Table.',
  TriggerAfterLoad:
    'Use this trigger to perform actions after a Cloud Data Store load is successfully executed.',
  TriggerAfterQuery:
    'Use this trigger to perform actions after a Data Store query is successfully executed.',
  TriggerAfterSave:
    'Use this trigger to perform actions after a Data Store is successfully saved.',
  TriggerBeforeCreate:
    'Use this trigger to perform actions before creating a new row in a Data Store. You can use this to populate default values using the `Set` action. The partial row used to create this new can be accessed inside expressions using `$._.new`.',
  TriggerBeforeLoad:
    'Use this trigger to perform actions before a Cloud Data Store load is issued. You can use this to set default filters that must in every load. The request object generated for this load can be accessed inside expressions using `$._.request`.',
  TriggerBeforeQuery: `Use this trigger to perform actions before a Data Store query is issued. You can use this to set default filters that must in every query.

The request object generated for this query can be accessed inside expressions using \`$._.request\`.

\`\`\`alert_warning
You may have to refresh the browser to see the changes in the query.
\`\`\`
`,
  TriggerBeforeSave:
    'Use this trigger to perform actions before a Data Store is saved. You can use this to perform any client side validations before issuing a save.',
  TriggerOnBlur: `Use this trigger to perform actions when the user tabs out of an input field.
  
> This will fire irrespective of whether the value has changed or not. 

\`\`\`alert_warning
Use the \`Trigger On Value Change\` trigger to perform actions only when the value has changed.
\`\`\`
`,
  TriggerOnCellClick:
    'Use this trigger to perform actions when the user click on a Data Grid cell.',
  TriggerOnChange:
    'Use this trigger to perform actions when the user changes the content of an input field.',
  TriggerOnClick:
    'Use this trigger to perform actions when the user performs a mouse click on the UI Component e.g. Button.',
  TriggerOnComment: `Use this trigger to perform actions when the user comments in the comments panel component. 
The comment information is available in the \`$._.comment\` object.
- \`$._.comment.commentText\` is the comment text.
- \`$._.comment.commentedBy\` is the user name of the user who commented.
- \`$._.comment.commentUid\` is the unique id of the comment.
- \`$._.comment.mentions\` is an array of unique user names mentioned in the comment.

\`\`\`javascript
// Access the comment information as below using expression
\`\${$._.comment.commentedBy} \${$._.comment.commentText} \${$._.comment.commentUid} \${$._.comment.mentions.join(',')}\`
\`\`\`
`,
  TriggerOnCreate: 'Use this trigger to perform actions on create',
  TriggerOnInitialize:
    'Use this trigger to perform actions after initilization is complete',
  TriggerOnDelete:
    'Use this trigger to perform actions before a Chip is being removed by the user.',
  TriggerOnDoubleClick:
    'Use this trigger to perform actions when the user performs a mouse doubleclick on the applicable UI Component.',
  TriggerOnDrop:
    'Use this trigger to perform actions when the user performs a drop in a Drag & Drop event on the Drop Target component.',
  TriggerOnDateChange:
    'Use this trigger to perform actions when the user selects the month on the calendar.',
  TriggerOnEventChange:
    'Use this trigger to perform actions when the user updates a calendar event by dragging or resizing an event.',
  TriggerOnEventClick:
    'Use this trigger to perform actions when the user clicks on a calendar event',
  TriggerOnEventCreate:
    'Use this trigger to perform actions when the user creates a calendar event by clicking on an empty slot on the calendar.',
  TriggerOnFocus:
    'Use this trigger to perform actions when the user tabs into an input field.',
  TriggerOnKeyDown:
    'Use this trigger to perform actions when the user presses any keyboard key on an input field.',
  TriggerOnMouseEnter:
    'Use this trigger to perform actions when the mouse enters the component.',
  TriggerOnMouseLeave:
    'Use this trigger to perform actions when the mouse leaves the component.',
  TriggerOnPageActive:
    'Use this trigger to perform actions when the user navigates to a page.',
  TriggerOnPageExit:
    'Use this trigger to perform actions when the user navigates out of a page.',
  TriggerOnPopoverClose:
    'Use this trigger to perform actions when a popover is closed.',
  TriggerOnPopoverOpen:
    'Use this trigger to perform actions when a popover is opened.',
  TriggerOnPopupClose:
    'Use this trigger to perform actions when a popup is closed.',
  TriggerOnPopupOpen:
    'Use this trigger to perform actions when a popup is opened.',
  TriggerOnResponse:
    'Use this trigger to perform actions when a response is received. The response object is available in the `$._.response` object.',
  TriggerOnRowClick:
    'Use this trigger to perform actions when the user performs a mouse click on a Data Grid row.',
  TriggerOnRowDoubleClick:
    'Use this trigger to perform actions when the user performs a mouse doubleclick on a Data Grid row.',
  TriggerOnSearch:
    'Use this trigger to perform actions after the user performs a search on the smart search component.',
  TriggerOnSubmit:
    'Use this trigger to perform actions on click of submit button of signature pad.',
  TriggerOnSubscriptionEvent: `Use this trigger to perform actions when a subscribed business event is received. Refer to \`Event Subscription\` component for more info on business events.
The event object provided which publishing the business event can be accessed using \`$._.event.data\`

e.g. when you publish an event using the below code on the server side scripts or cloud functions
\`\`\`javascript
await db.publish(context, contextId, {someValue: 100});
\`\`\`

The variable \`someValue\` passed above can be accessing using the below expression in any action under this trigger.
\`\`\`javascript
$._.event.data.someValue
\`\`\`

`,
  TriggerOnTabActive:
    'Use this trigger to perform actions when a tab in a tab panel is activated either programmatically or by the user clicking on the tab header.',
  TriggerOnTabExit:
    'Use this trigger to perform actions when a tab in a tab panel is exited either programmatically or by the user action.',
  TriggerOnInterval:
    'Use this trigger to perform actions when a tab in a tab panel is activated either programmatically or by the user clicking on the tab header.',
  TriggerOnMediumScreen:
    'Use this trigger to perform actions when the screen size changes to medium or it is medium when activated.',
  TriggerOnMention: `Use this trigger to perform actions when a user is mentioned in the comments. The mentioned user can be accessed using the expression \`$._.comment.mention\` and the comment text can be accessed using the expression \`$._.comment.comment\`.

\`\`\`alert
Note: This trigger will be executed once for each user mentioned in the comment. If a user is mentioned multiple times in the comment, this trigger will be executed only once for that user. If you want to send a single notification to all the mentioned users, you can use the \`Trigger on Comment\`.
\`\`\`

\`\`\`javascript
// Access the comment information as below using expression
\`\${$._.comment.commentedBy} \${$._.comment.commentText} \${$._.comment.commentUid} \${$._.comment.mention}\`
\`\`\`

`,
  TriggerOnLargeScreen:
    'Use this trigger to perform actions when the screen size changes to large or it is large when activated.',
  TriggerOnSmallScreen:
    'Use this trigger to perform actions when the screen size changes to small or it is small when activated.',
  TriggerOnExtraSmallScreen:
    'Use this trigger to perform actions when the screen size changes to extra small or it is extra small when activated.',
  TriggerOnExtraLargeScreen:
    'Use this trigger to perform actions when the screen size changes to extra large or it is extra large when activated.',
  TriggerOnPortraitScreen:
    'Use this trigger to perform actions when the screen orientation changes to portrait or it is portrait when activated.',
  TriggerOnLandscapeScreen:
    'Use this trigger to perform actions when the screen orientation changes to landscape or it is landscape when activated.',
  TriggerOnValueChange:
    'Use this trigger to perform actions on blur when the value of the input field changes.',
  TriggerOnWindowSizeChange:
    'Use this trigger to perform actions on page load and everytime the window size is changed.',
};

const getDefaultChildren = (type: TriggerItemType): ItemType[] => {
  if (type === 'TriggerBeforeQuery') {
    return ['ActionSetQueryFilter'];
  }
  if (type === 'TriggerBeforeCreate') {
    return ['ActionSet'];
  }
  return [];
};

const TriggersManager =
  <K extends TriggerItemType>(): TriggerManagerFn<K> =>
  (platform: Platform, type: K): TriggerManager<K> => {
    const mgr: TriggerManager<K> = {
      ...baseTriggerManager(platform, type),
      getDisplayText: (item) => item.props.disableCondition ?? '',
      isChildAllowed: (page, parent, childType, { appUid, pageId }) => {
        if (getManager(childType).managerType !== 'Action') {
          return false;
        }
        switch (parent.type) {
          case 'TriggerBeforeLoad': {
            return (
              [
                'ActionAbort',
                'ActionLogToConsole',
                'ActionSetSimpleQueryFilter',
                'ActionShowMessage',
              ] as ActionItemType[]
            ).includes(childType as ActionItemType);
          }
          case 'TriggerBeforeQuery': {
            if (
              (
                [
                  'ActionAbort',
                  'ActionCopyFilters',
                  'ActionLogToConsole',
                  'ActionSetAggregateAttribute',
                  'ActionSetDistinct',
                  'ActionSetFilterCombiner',
                  'ActionSetFilterCombiner',
                  'ActionSetGroupByAttribute',
                  'ActionSetProjection',
                  'ActionSetQueryFilter',
                  'ActionSetSimpleQueryFilter',
                  'ActionSetSort',
                  'ActionShowMessage',
                ] as ActionItemType[]
              ).includes(childType as ActionItemType)
            ) {
              return true;
            }

            if (
              (
                ['ActionQuery', 'ActionQueryChart'] as ActionItemType[]
              ).includes(childType as ActionItemType)
            ) {
              // allow execution of query inside TriggerBeforeQuery
              // if copy filters exists
              const copyFilters = platform.getChildrenOfTypeById(
                appUid,
                pageId,
                parent.itemId,
                'ActionCopyFilters',
              );
              return copyFilters.length > 0;
            }
            return false;
          }
          case 'TriggerBeforeCreate': {
            return (
              [
                'ActionAbort',
                'ActionLogToConsole',
                'ActionSet',
                'ActionShowMessage',
              ] as ActionItemType[]
            ).includes(childType as ActionItemType);
          }
          case 'TriggerBeforeSave': {
            return (
              [
                'ActionAbort',
                'ActionFocus',
                'ActionLogToConsole',
                'ActionLoopRecords',
                'ActionMatchRecord',
                'ActionShowMessage',
                'ActionUserConfirmation',
              ] as ActionItemType[]
            ).includes(childType as ActionItemType);
          }
          default:
        }
        return true;
      },
      defaultValue: triggerDefaultValue as Required<TriggerItemTypeRegistry[K]>,
      defaultChildren: getDefaultChildren(type),
      props: {
        triggerOnTab: {
          label: 'Trigger On Tab',
          type: 'boolean',
          helpText: 'Enable triggering when the Tab key is pressed.',
        },
        disableCondition: {
          label: 'Trigger Condition',
          type: 'expressionOnly',
          returnType: (item, context) => 'boolean',
          helpText:
            'This trigger will be executed only if this condition is either blank or returns true.',
        },
      } as PropertyDefinitions<
        Omit<TriggerItemTypeRegistry[K], keyof CommonProps>
      >,
      name: triggerNames[type],
      managerType: 'Trigger',
      canHaveChildren: () => true,
      isPropertyVisible(prop): boolean {
        switch (prop) {
          case 'triggerOnTab':
            return type === 'TriggerOnEnterKeyPress';
          default:
            return true;
        }
      },
      replaceableWith: (item) => {
        switch (item.type) {
          case 'TriggerOnComment':
          case 'TriggerOnMention':
            return ['TriggerOnComment', 'TriggerOnMention'];
          case 'TriggerOnMouseEnter':
          case 'TriggerOnMouseLeave':
            return ['TriggerOnMouseEnter', 'TriggerOnMouseLeave'];
          case 'TriggerOnDoubleClick':
            return ['TriggerOnClick', 'TriggerOnDoubleClick'];
          case 'TriggerBeforeQuery':
          case 'TriggerAfterQuery':
            return ['TriggerBeforeQuery', 'TriggerAfterQuery'];
          case 'TriggerBeforeLoad':
          case 'TriggerAfterLoad':
            return ['TriggerBeforeLoad', 'TriggerAfterLoad'];
          case 'TriggerBeforeCreate':
          case 'TriggerBeforeSave':
          case 'TriggerAfterSave':
            return [
              'TriggerBeforeCreate',
              'TriggerBeforeSave',
              'TriggerAfterSave',
            ];
          case 'TriggerOnEventChange':
          case 'TriggerOnEventClick':
          case 'TriggerOnEventCreate':
            return [
              'TriggerOnEventChange',
              'TriggerOnEventClick',
              'TriggerOnEventCreate',
            ];
          case 'TriggerOnRowClick':
          case 'TriggerOnRowDoubleClick':
            return ['TriggerOnRowClick', 'TriggerOnRowDoubleClick'];
          case 'TriggerOnPageActive':
          case 'TriggerOnPageExit':
            return ['TriggerOnPageActive', 'TriggerOnPageExit'];
          case 'TriggerOnPopupClose':
          case 'TriggerOnPopupOpen':
            return ['TriggerOnPopupClose', 'TriggerOnPopupOpen'];
          case 'TriggerOnPopoverClose':
          case 'TriggerOnPopoverOpen':
            return ['TriggerOnPopoverClose', 'TriggerOnPopoverOpen'];
          case 'TriggerOnBlur':
          case 'TriggerOnFocus':
          case 'TriggerOnKeyDown':
            return ['TriggerOnBlur', 'TriggerOnFocus', 'TriggerOnKeyDown'];
          case 'TriggerOnSmallScreen':
          case 'TriggerOnMediumScreen':
          case 'TriggerOnLargeScreen':
          case 'TriggerOnExtraSmallScreen':
          case 'TriggerOnExtraLargeScreen':
          case 'TriggerOnPortraitScreen':
          case 'TriggerOnLandscapeScreen':
          case 'TriggerOnWindowSizeChange':
            return [
              'TriggerOnSmallScreen',
              'TriggerOnMediumScreen',
              'TriggerOnLargeScreen',
              'TriggerOnExtraLargeScreen',
              'TriggerOnExtraSmallScreen',
              'TriggerOnPortraitScreen',
              'TriggerOnLandscapeScreen',
              'TriggerOnWindowSizeChange',
            ];
          case 'TriggerOnChange':
          case 'TriggerOnValueChange':
            return ['TriggerOnChange', 'TriggerOnValueChange'];
        }
        return [];
      },
      icon: triggerIcons[type] ?? { iconName: 'rocket' },
      analyze: analyzeTrigger,
      description: triggerDesc[type],
      helpRenders: helpRenders[type] ?? [],
    };
    return mgr;
  };

export default TriggersManager;
